import {
  BaseResponse,
  PayrollCompanySetting,
  SalaryAccount,
} from './types';
import {APIClient, useAuthContext, useClient} from '../context/auth-context';
import {useMutation, useQuery, QueryClient} from 'react-query';
import {toast} from 'react-toastify';

type OptionResponse = BaseResponse<{item: string; value: string}[]>;
export function getOptions(client: APIClient, category: string) {
  return client.get<OptionResponse>(`payroll/hn/option?category=${category}`);
}
export function getCalendarEvent(client: APIClient, year: number) {
  return client.get<any>(`payroll/calendar/event?year=${year}`);
}
export function getAccountCategory(client: APIClient) {
  return client.get<any>(`payroll/hn/salary/accountCategory`);
}
export function getSalaryAccounts(client: APIClient, companyId: string) {
  return client.get<any>(`payroll/company/${companyId}/salary/account`);
}
export function saveSalaryAccounts(client: APIClient, companyId: string, data: SalaryAccount[]) {
  return client.put<any>(`payroll/company/${companyId}/salary/account`, { data });
}

export function usePayrollOptions(category: string) {
  const client = useClient();
  return useQuery(['payroll-options', category], () =>
    getOptions(client, category).then(resp => resp.data),
  );
}

export function getData(client: APIClient, companyId: string) {
  return client.get<BaseResponse<PayrollCompanySetting>>(
    `payroll/company/${companyId}/company/setting`,
  );
}

export function saveData(
  client: APIClient,
  companyId: string,
  data: PayrollCompanySetting,
) {
  return client.put<BaseResponse<any>>(
    `payroll/company/${companyId}/company/setting`,
    {
      data,
    },
  );
}

export function useSaveMutate(actions: any) {
  const client = useClient();
  const {companyId} = useAuthContext();
  return useMutation(
    (data: PayrollCompanySetting) => saveData(client, companyId, data),
    {
      ...actions,
      onError: (error: {code: number; message: string}) => {
        if (error.code !== 500 && error.message !== 'NotEnablePY') {
          toast.error(error.message || '儲存失敗', {
            hideProgressBar: true,
            position: 'top-center',
            autoClose: false,
          });
        }
      },
    },
  );
}

export function confirm(client: APIClient, companyId: string) {
  return client.put(`payroll/company/${companyId}/company/setting/confirm`);
}

export function useConfirmMutate(actions: any) {
  const client = useClient();
  const {companyId} = useAuthContext();
  return useMutation(
    (data: PayrollCompanySetting) =>
      saveData(client, companyId, data).then(() => confirm(client, companyId)),
    {
      ...actions,
      onError: (error: {code: number; message: string}) => {
        if (error.code !== 500 && error.message !== 'NotEnablePY') {
          toast.error(error.message || '儲存失敗', {
            hideProgressBar: true,
            position: 'top-center',
            autoClose: false,
          });
        }
      },
    },
  );
}

export function useCalendarEvent(year?: number) {
  const client = useClient();
  return useQuery(
    ['calendar-event', year],
    () =>
      getCalendarEvent(client, year || new Date().getFullYear()).then(
        resp => resp.data,
      ),
    {
      enabled: year !== undefined,
    },
  );
}

export function useAccountCategory() {
  const client = useClient();
  return useQuery(['account-category'], () =>
    getAccountCategory(client).then(resp => resp.data),
  );
}

export function useSalaryAccounts() {
  const client = useClient();
  const {companyId} = useAuthContext();
  return useQuery(
    ['salary-accounts'],
    () =>
      getSalaryAccounts(client, companyId).then(
        resp => resp.data,
      ),
    {
      refetchOnWindowFocus: true,
    }
  );
}

export function useSaveSalaryAccounts() {
  const client = useClient();
  const {companyId} = useAuthContext();
  return useMutation(
    (data: SalaryAccount[]) => saveSalaryAccounts(client, companyId, data),
    {
      onError: (error: {code: number; message: string}) => {
        if (error.code !== 500 && error.message !== 'NotEnablePY') {
          toast.error(error.message || '儲存失敗', {
            hideProgressBar: true,
            position: 'top-center',
            autoClose: false,
          });
        }
      },
    },
  );
}